import { useQuery, useMutation, useQueryClient } from "react-query";
import api from "../config/api";

export interface HistoryEvent {
  id: string; // UUID
  type:
    | "created"
    | "renewed"
    | "edited"
    | "snoozed"
    | "assigned"
    | "reminder_sent"
    | "acknowledged" // external stakeholder clicked Acknowledge in email
    | "archived"
    | "restored";
  description: string;
  date: string; // ISO string
  user: string;
  // Present on company-wide audit trail rows only
  item_id?: string;
  item_name?: string;
  item_category?: string;
  meta?: {
    previousDate?: string;
    newDate?: string;
    field?: string;
    previousValue?: string;
    newValue?: string;
    [key: string]: unknown;
  };
}

export interface ExpiryItem {
  id: string; // UUID
  name: string;
  category: string;
  expiry_date: string;
  quantity?: number;
  unit?: string;
  notes?: string;
  snoozed_until?: string | null; // YYYY-MM-DD; NULL = not snoozed
  owners?: {
    id: string | null;
    name: string;
    email: string;
    avatar?: string | null;
  }[];
  owner_emails?: string[]; // raw unregistered emails (read-only from backend)
  history?: HistoryEvent[];
  created_at?: string;
  updated_at?: string;
}

export const useExpiryItems = () => {
  return useQuery<ExpiryItem[]>("expiryItems", async () => {
    const response = await api.get("/expiry");
    return response.data;
  });
};

export const useExpiryItem = (id: string) => {
  return useQuery<ExpiryItem>(
    ["expiryItem", id],
    async () => {
      const response = await api.get(`/expiry/${id}`);
      return response.data;
    },
    { enabled: !!id }
  );
};

export const useExpiringSoon = (days: number = 7) => {
  return useQuery<ExpiryItem[]>(["expiringSoon", days], async () => {
    const response = await api.get(`/expiry/expiring-soon?days=${days}`);
    return response.data;
  });
};

export const useCreateExpiryItem = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (item: Omit<ExpiryItem, "id" | "created_at" | "updated_at">) => {
      const response = await api.post("/expiry", item);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("expiryItems");
        queryClient.invalidateQueries("expiringSoon");
      },
    }
  );
};

export interface UpdateExpiryItemPayload extends Partial<ExpiryItem> {
  id: string; // UUID
  isRenewal?: boolean;
  isExpiryEdit?: boolean;
  owner_emails?: string[];
  owner_email?: string;
  owner_id?: string | null; // UUID
}

export const useUpdateExpiryItem = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, ...updates }: UpdateExpiryItemPayload) => {
      const response = await api.put(`/expiry/${id}`, updates);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("expiryItems");
        queryClient.invalidateQueries("expiringSoon");
      },
    }
  );
};

export interface MockUser {
  id: string; // UUID
  name: string;
  email: string;
  avatar: string | null;
}

export const useUsers = () => {
  return useQuery<MockUser[]>("users", async () => {
    const response = await api.get("/users");
    return response.data;
  });
};

export const useDeleteExpiryItem = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: string) => {
      const response = await api.delete(`/expiry/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("expiryItems");
        queryClient.invalidateQueries("expiringSoon");
      },
    }
  );
};

export const useArchivedItems = () => {
  return useQuery<ExpiryItem[]>("archivedItems", async () => {
    const response = await api.get("/expiry/archived");
    return response.data;
  });
};

export const useArchiveItem = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (id: string) => {
      const response = await api.post(`/expiry/${id}/archive`, {});
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("expiryItems");
        queryClient.invalidateQueries("archivedItems");
        queryClient.invalidateQueries("expiringSoon");
      },
    }
  );
};

export const useRestoreItem = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (id: string) => {
      const response = await api.post(`/expiry/${id}/restore`, {});
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("expiryItems");
        queryClient.invalidateQueries("archivedItems");
        queryClient.invalidateQueries("expiringSoon");
      },
    }
  );
};

export interface SendReminderResult {
  sent: number;
  recipients: string[];
  failed?: string[];
}

/**
 * useItemHistory
 *
 * Fetches history events for a single item from GET /api/expiry/:id/history.
 * Only fires when `id` is non-empty and `enabled` is true (e.g. history tab active).
 * Falls back to `item.history` when running in mock mode (history is embedded).
 */
export const useItemHistory = (id: string, enabled: boolean = true) => {
  return useQuery<HistoryEvent[]>(
    ["itemHistory", id],
    async () => {
      const response = await api.get(`/expiry/${id}/history`);
      return response.data as HistoryEvent[];
    },
    {
      enabled: !!id && enabled,
      staleTime: 30_000, // cache for 30s — history doesn't change that often
    }
  );
};

/**
 * useAllHistory
 *
 * Fetches the company-wide audit trail from GET /api/expiry/history.
 * Used exclusively by the AuditTrailPage (Pro+ feature).
 * Supports pagination: pass { limit, offset }.
 */
export const useAllHistory = (params?: { limit?: number; offset?: number }) => {
  const limit = params?.limit ?? 200;
  const offset = params?.offset ?? 0;
  return useQuery<HistoryEvent[]>(
    ["allHistory", limit, offset],
    async () => {
      const response = await api.get(
        `/expiry/history?limit=${limit}&offset=${offset}`
      );
      return response.data as HistoryEvent[];
    },
    {
      staleTime: 60_000, // 1 min cache — full audit trail is less time-sensitive
      keepPreviousData: true,
    }
  );
};

// ── Import result shape ────────────────────────────────────────────────────────
export interface ImportCsvResult {
  imported: number;
  ready: number;
  draft: number;
  errors: string[];
  items: ExpiryItem[];
}

/**
 * Calls POST /api/expiry/import-csv with raw CSV text.
 * The backend parses, validates, and bulk-inserts — much more efficient
 * than sending one POST /expiry per row.
 */
export const useImportCsv = () => {
  const queryClient = useQueryClient();
  return useMutation<ImportCsvResult, Error, string>(
    async (csvText: string) => {
      const response = await api.post("/expiry/import-csv", { csv: csvText });
      return response.data as ImportCsvResult;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("expiryItems");
        queryClient.invalidateQueries("expiringSoon");
      },
    }
  );
};

// ── AI document extraction ────────────────────────────────────────────────────

export interface AIExtractionResult {
  expiry_date: string | null; // ISO YYYY-MM-DD or null
  document_name: string | null; // official title of the document
  category: string | null; // one of the predefined categories or null
  notes: string | null; // brief one-sentence summary
  confidence: "high" | "medium" | "low";
  raw_text_snippet: string;
  reasoning: string;
}

export interface UploadDocumentResult {
  upload: Record<string, any>;
  extracted: AIExtractionResult;
}

/**
 * useUploadDocument
 *
 * Calls POST /api/documents/upload (multipart/form-data).
 * Pass a FormData that contains a `file` field.
 * The axios instance handles auth token injection automatically.
 * Do NOT set Content-Type manually — let axios set the multipart boundary.
 */
export const useUploadDocument = () =>
  useMutation<UploadDocumentResult, Error, FormData>(
    async (formData: FormData) => {
      const response = await api.post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data as UploadDocumentResult;
    }
  );

/** Triggers POST /api/expiry/:id/send-reminder — sends a real email to all owners. */
export const useSendReminderEmail = () => {
  const queryClient = useQueryClient();
  return useMutation<SendReminderResult, Error, string>(
    async (id: string) => {
      const response = await api.post(`/expiry/${id}/send-reminder`, {});
      return response.data as SendReminderResult;
    },
    {
      // Refresh item to reflect the new "reminder_sent" history entry
      onSuccess: () => {
        queryClient.invalidateQueries("expiryItems");
      },
    }
  );
};
